import { isEqual } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  AdvanceFilters,
  BoxV2,
  Checkbox,
  LoadingSkeleton,
  Switch,
  TableListV2,
  useQueryFetch
} from 'sonata-react-components';
import { DeviceMakeModelBidderDashboardResponse } from 'sonata-static-storage/lib/common/common.types';

import { MakeModelsSelectionStyled } from './MakeModelsSelection.styled';

import type { DeviceMakeModelType } from 'sonata-static-storage/lib/schemas/targeting/DeviceMakeModel/DeviceMakeModels.types';

type Props = {
  getDeviceMakeModels: () => Promise<DeviceMakeModelBidderDashboardResponse[]>;
  id: string;
  makeModels: DeviceMakeModelType[];
  onChangeMakeModels: (newMakeModels: DeviceMakeModelType[]) => void;
};

type State = {
  filters: { advanceFilters: { id: string; value: any }[]; text?: string };
};

type OnFilter = {
  onFilterStatus?: (state: { id: string; value: any }[]) => { id: string; value: any }[];
};

const generateItemId = ({ make, model }: DeviceMakeModelBidderDashboardResponse): string => {
  return `${make}_${model ?? 'all'}`;
};

export type MakeModelDataEntry = {
  id: string;
  make: string;
  model?: string;
  normalizedMake: string;
  normalizedModel: string;
};

export function MakeModelsSelection({ getDeviceMakeModels, id, makeModels, onChangeMakeModels }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const [{ filters }, setState] = useState<State>({
    filters: {
      advanceFilters: [
        {
          id: `${id}_checkbox_allModels`,
          value: false
        }
      ],
      text: undefined
    }
  });

  const { data = [], status } = useQueryFetch<DeviceMakeModelBidderDashboardResponse[]>(
    `${id}-makeModels`,
    getDeviceMakeModels,
    {
      cacheTime: 0,
      retry: 0,
      staleTime: 0
    }
  );

  const handleAdvancedFiltersChanged = ({
    advanceFilters,
    text
  }: {
    advanceFilters: { id: string; value: any }[];
    text: string;
  }) => {
    const allModelsEnabled = advanceFilters.some(({ id: filterId, value: filterValue }) => {
      return filterId === `${id}_checkbox_allModels` && !!filterValue;
    });
    const newTextFilterValue = allModelsEnabled ? 'allModels' : text;
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        advanceFilters,
        text: newTextFilterValue
      }
    }));
  };

  const listFilters = useMemo(
    () => ({
      text: filters.text
    }),
    [filters.text]
  );

  const handleMakeModelsOnChanged = useCallback(
    (newSelection: MakeModelDataEntry[]) => {
      const newMakeModels = newSelection.map(({ make, model }) => ({
        make,
        model: model === 'allModels' ? undefined : model
      }));
      onChangeMakeModels(newMakeModels);
    },
    [onChangeMakeModels]
  );

  function CheckboxLeftFilter({ onFilterStatus }: OnFilter) {
    const checkboxId = `${id}_checkbox_allModels`;
    const handleCheckboxChanged = (_id: string, newState: boolean) => {
      onFilterStatus?.([
        {
          id: checkboxId,
          value: newState
        }
      ]);
    };

    return (
      <div className="leftFiltersContainer__leftFilter">
        <Checkbox
          id={checkboxId}
          labelText={'Show only "All Models" options'}
          onClickAction={handleCheckboxChanged}
          type="checkbox"
        />
        <span className="bodyDefault">While this option is selected, users will not be able to search by text</span>
      </div>
    );
  }

  const leftFilters: React.ComponentProps<typeof AdvanceFilters>['leftFilters'] = useMemo(
    () => [
      {
        className: 'leftFiltersContainer',
        component: <CheckboxLeftFilter />,
        id: `${id}_allModels`,
        sendCallback: true
      }
    ],
    []
  );

  const makeModelsColumns = useMemo(
    () => [
      {
        Cell: ({
          cell: {
            row: { original: rowData }
          }
        }: {
          cell: { row: { original: MakeModelDataEntry } };
        }) => {
          const { model, normalizedMake, normalizedModel } = rowData;
          return (
            <Switch>
              <Switch.Case condition={!!model}>
                <div className="makeModelEntry__model textBodyDefault bodyDefault">
                  <span title={normalizedModel}>{normalizedModel}</span>
                </div>
                <div className="makeModelEntry__make">
                  <span title={normalizedMake}>{normalizedMake}</span>
                </div>
              </Switch.Case>
              <Switch.Case condition={!model}>
                <div className="makeModelEntry__model textBodyDefault bodyDefault">
                  <span
                    title={formatMessage({
                      defaultMessage: 'All Models',
                      id: 'targetingDeviceMakeModels.list.allModels'
                    })}
                  >
                    {formatMessage({
                      defaultMessage: 'All Models',
                      id: 'targetingDeviceMakeModels.list.allModels'
                    })}
                  </span>
                </div>
                <div className="makeModelEntry__make">
                  <span title={normalizedMake}>{normalizedMake}</span>
                </div>
              </Switch.Case>
            </Switch>
          );
        },
        Header: 'Name',
        accessor: 'makeModelEntry',
        className: 'makeModelEntry',
        customCell: true,
        textAlign: 'left'
      }
    ],
    [formatMessage]
  );

  const makeModelsColumnsData: MakeModelDataEntry[] = useMemo(() => {
    if (status === 'success' && data) {
      return data.map(makeModel => {
        return {
          ...makeModel,
          id: generateItemId(makeModel),
          model: makeModel.model ?? 'allModels'
        };
      });
    }
    return [];
  }, [status, data, filters.text]);

  const defaultSelectedRows = useMemo(() => {
    return data.reduce((acc, makeModel) => {
      const { make, model } = makeModel;
      const makeModelIsSelected = makeModels.some(({ make: selectedMake, model: selectedModel }) => {
        return isEqual(selectedMake, make) && isEqual(selectedModel, model);
      });
      if (makeModelIsSelected) {
        return {
          ...acc,
          [generateItemId(makeModel)]: true
        };
      }
      return acc;
    }, {});
  }, [makeModels, makeModelsColumnsData, filters.text]);

  return (
    <MakeModelsSelectionStyled className="makeModelsSelection">
      <AdvanceFilters
        className="makeModelsSelection__advancedFilters"
        disabled={filters?.text === 'allModels'}
        leftFilters={leftFilters}
        onChange={handleAdvancedFiltersChanged}
      />
      <BoxV2 id={`${id}_make_models_box_inner_container`}>
        <BoxV2.Body className="makeModelsSelection__tableContainer" padding="0">
          <Switch>
            <Switch.Case condition={status === 'loading'}>
              <div className="makeModelsSelection__loading">
                <div className="makeModelsSelection__loadingToolbar">
                  <LoadingSkeleton count={1} height={32} width="100%" />
                </div>
                <div className="makeModelsSelection__loadingContent">
                  <LoadingSkeleton count={5} height={59} width="100%" />
                </div>
                <div className="makeModelsSelection__loadingPagination">
                  <LoadingSkeleton count={1} height={44} width="100%" />
                </div>
              </div>
            </Switch.Case>
            <Switch.Default>
              <TableListV2
                className="makeModelsSelection__tableContainer__tableData"
                columns={makeModelsColumns}
                config={{
                  maxHeight: 450,
                  numberOfColumns: 4,
                  onChangeSelection: handleMakeModelsOnChanged,
                  pagination: {
                    show: true,
                    size: 20,
                    type: 'compact'
                  },
                  searchEmptyState: {
                    description: formatMessage({
                      defaultMessage: 'Please change your search terms and try again',
                      id: 'targetingDeviceMakeModels.list.emptyState.description'
                    }),
                    icon: 'fal fa-empty-set',
                    show: true,
                    title: formatMessage({
                      defaultMessage: 'Your search did not match any result',
                      id: 'targetingDeviceMakeModels.list.emptyState.title'
                    })
                  },
                  selectableItem: true,
                  toolbar: {
                    id: `${id}_toolbar`,
                    showSelectAll: true
                  },
                  type: 'list'
                }}
                data={makeModelsColumnsData}
                defaultSelectedRows={defaultSelectedRows}
                filters={listFilters}
                id={`${id}_deviceMakeModelsSelection_list`}
                isSelectableList
              />
            </Switch.Default>
          </Switch>
        </BoxV2.Body>
      </BoxV2>
    </MakeModelsSelectionStyled>
  );
}
